/**
 * TOSS Web Application — Client portal: switching the active legal entity
 *
 * The mutation plus the two sentences a customer sees when it fails, extracted so the two
 * controls that switch companies cannot drift apart on either.
 *
 * There are two of them, and they look nothing alike on purpose:
 *
 *  - `ClientCompanySwitcher` — the chip / app-bar title, a `DropdownPortal` over the header;
 *  - `ClientProfileCompanyCard` — the «Работаю от имени» card of the «Профиль» screen, whose
 *    list is a sheet (mobile rework 2026-08-21, reshaped 26.08.2026).
 *
 * What must NOT differ between them is the behaviour: selecting the company you are already
 * in closes the list and writes nothing; a 404 means an administrator revoked the membership
 * between render and click and says so in its own words, because «не удалось переключить» in
 * that case sends the customer to retry something that will never succeed.
 *
 * The «what a switch actually changes» sequence lives in `applyCompanySwitch`, reached
 * through `useSwitchCompany` — see that mutation's docblock.
 */

import { useCallback } from 'react';
import { isAxiosError } from '@/api/client';
import { useSwitchCompany } from '@/hooks/api/clientPortal/useClientProfile';
import { useToast } from '@/store/uiStore';

// ============================================================================
// Types
// ============================================================================

export interface ClientCompanySwitchHandler {
  /**
   * Switch to `companyId`, or just close the list when it is already the active one.
   * `onSettled` runs in every outcome — it is what closes the control.
   */
  switchTo: (companyId: string, activeCompanyId: string, onSettled: () => void) => void;
  /** A switch is in flight: both controls disable themselves rather than queue a second one. */
  isPending: boolean;
}

// ============================================================================
// Hook
// ============================================================================

export function useClientCompanySwitchHandler(): ClientCompanySwitchHandler {
  const switchCompany = useSwitchCompany();
  const { error: showError } = useToast();

  const switchTo = useCallback(
    (companyId: string, activeCompanyId: string, onSettled: () => void): void => {
      if (companyId === activeCompanyId) {
        onSettled();
        return;
      }

      switchCompany.mutate(companyId, {
        onSuccess: onSettled,
        onError: (error) => {
          onSettled();
          const isRevoked = isAxiosError(error) && error.response?.status === 404;
          showError(
            isRevoked ? 'Доступ к этой компании отозван' : 'Не удалось переключить компанию',
            isRevoked
              ? 'Список компаний обновлён.'
              : 'Проверьте соединение и попробуйте ещё раз.'
          );
        },
      });
    },
    [switchCompany, showError]
  );

  return { switchTo, isPending: switchCompany.isPending };
}
